/**
 * Which regions a user can switch to from the sidebar.
 *
 * Pulled out of the sidebar because the rule decides whether a control appears at all, and it
 * was buried in a seven-hundred-line component where neither branch could be exercised: the
 * person testing it is a Super Admin, so the empty case never renders for them.
 */

export interface SwitchableSite {
  region: string;
  label?: string | null;
  url?: string | null;
}

/*
  Trimmed as well as lowercased. The backend's getAllRegions() can yield space-padded region
  codes, and an untrimmed comparison silently drops those regions from the switcher — a bug that
  looks like missing permissions rather than stray whitespace.
*/
const normalise = (value: string): string => value.trim().toLowerCase();

/**
 * The regions this user may move to, excluding the one they are already in.
 *
 * A Super Admin reaches every deployed site. Everyone else reaches only the regions recorded on
 * their own account — an empty or missing list means they stay where they are.
 */
export const switchableRegions = <T extends SwitchableSite>(
  sites: T[] | null | undefined,
  currentRegion: string,
  isSuperAdmin: boolean,
  userRegions: string[] | null | undefined
): T[] => {
  const all = sites ?? [];
  const here = normalise(currentRegion);
  const allowed = new Set(
    isSuperAdmin ? all.map((site) => normalise(site.region)) : (userRegions ?? []).map(normalise)
  );
  return all.filter((site) => {
    const key = normalise(site.region);
    return key !== here && allowed.has(key);
  });
};
